import React, { useState, useEffect } from 'react';
import { Store, CheckCircle, XCircle, Loader2, AlertCircle, Truck, Package, Key } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const Settings = () => {
  const { agent } = useAuth();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [syncOptions, setSyncOptions] = useState({});
  const [formData, setFormData] = useState({});
  const [tcsConfig, setTcsConfig] = useState({
    auth_type: 'bearer',
    bearer_token: '',
    token_expiry: '',
    username: '',
    password: '',
    customer_no: ''
  });
  const [tcsConfigured, setTcsConfigured] = useState(false);
  const [savingTcs, setSavingTcs] = useState(false);
  const [syncingDtdc, setSyncingDtdc] = useState(false);
  const [syncingTcs, setSyncingTcs] = useState(false);
  const [syncingAllDtdc, setSyncingAllDtdc] = useState(false);
  
  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchStores();
    fetchTcsConfig();
  }, []);

  const fetchStores = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stores`);
      if (!response.ok) throw new Error('Failed to fetch stores');
      const data = await response.json();
      setStores(data);
      
      // Initialize form data
      const initialFormData = {};
      data.forEach(store => {
        initialFormData[store.store_name] = {
          shopify_domain: store.shopify_domain || '',
          shopify_token: store.shopify_token || '',
        };
      });
      setFormData(initialFormData);
    } catch (error) {
      console.error('Error fetching stores:', error);
      toast.error('Failed to load stores');
    } finally {
      setLoading(false);
    }
  };

  const fetchTcsConfig = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tcs/credentials`);
      if (!response.ok) throw new Error('Failed to fetch TCS config');
      const data = await response.json();
      
      if (data.configured) {
        setTcsConfigured(true);
        setTcsConfig(prev => ({
          ...prev,
          auth_type: data.auth_type || 'bearer',
          customer_no: data.customer_no || ''
        }));
      }
    } catch (error) {
      console.error('Error fetching TCS config:', error);
    }
  };

  const handleInputChange = (storeName, field, value) => {
    setFormData(prev => ({
      ...prev,
      [storeName]: {
        ...prev[storeName],
        [field]: value
      }
    }));
  };

  const handleSaveStore = async (storeName) => {
    const data = formData[storeName];
    
    if (!data.shopify_domain || !data.shopify_token) {
      toast.error('Both Shopify domain and API token are required');
      return;
    }

    setSaving(storeName);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/shopify/configure?store_name=${storeName}&shopify_domain=${data.shopify_domain}&shopify_token=${data.shopify_token}`,
        { method: 'POST' }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to configure Shopify');
      }
      
      const result = await response.json();
      toast.success(`Shopify configured successfully for ${storeName}!`);
      
      // Refresh stores to get updated status
      await fetchStores();
    } catch (error) {
      console.error('Error configuring Shopify:', error);
      toast.error(error.message || 'Failed to configure Shopify');
    } finally {
      setSaving(null);
    }
  };

  const handleSyncStore = async (storeName) => {
    setSaving(`sync-${storeName}`);
    
    try {
      toast.info(`Starting recent orders sync for ${storeName}...`);
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/shopify/sync/${storeName}?full_sync=true`,
        { 
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to sync orders');
      }
      
      const result = await response.json();
      
      if (result.success) {
        toast.success(`Synced ${result.orders_synced || 0} recent orders from ${storeName}!`);
        await fetchStores();
      } else {
        toast.error(result.message || 'Sync completed with issues');
      }
    } catch (error) {
      console.error('Error syncing store:', error);
      toast.error(error.message || 'Failed to sync orders');
    } finally {
      setSaving(null);
    }
  };

  const handleSyncAllComplete = async (storeName) => {
    setSaving(`sync-all-${storeName}`);
    
    try {
      const startOrder = syncOptions[storeName]?.startOrder || null;
      const direction = syncOptions[storeName]?.direction || 'newest_first';
      
      const message = startOrder 
        ? `Starting sync from order #${startOrder} (${direction === 'oldest_first' ? 'oldest first' : 'newest first'})...`
        : `Starting COMPLETE historical sync for ${storeName}...`;
      
      toast.info(message);
      
      const url = new URL(`${process.env.REACT_APP_BACKEND_URL}/api/shopify/sync-all-complete/${storeName}`);
      if (startOrder) {
        url.searchParams.append('start_order_number', startOrder);
        url.searchParams.append('direction', direction);
      }
      
      const response = await fetch(url.toString(), { 
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to sync all orders');
      }
      
      const result = await response.json();
      
      if (result.success) {
        toast.success(`Successfully synced ${result.orders_synced || 0} complete orders from ${storeName}!`);
        toast.info('All historical orders have been imported');
        await fetchStores();
      } else {
        toast.error(result.message || 'Sync completed with issues');
      }
    } catch (error) {
      console.error('Error syncing all orders:', error);
      toast.error(error.message || 'Failed to sync all orders');
    } finally {
      setSaving(null);
    }
  };

  const handleTcsConfigChange = (field, value) => {
    setTcsConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveTcsConfig = async () => {
    if (tcsConfig.auth_type === 'bearer') {
      if (!tcsConfig.bearer_token) {
        toast.error('Bearer token is required');
        return;
      }
    } else {
      if (!tcsConfig.username || !tcsConfig.password) {
        toast.error('Username and password are required');
        return;
      }
    }

    setSavingTcs(true);

    try {
      const payload = {
        auth_type: tcsConfig.auth_type
      };

      if (tcsConfig.auth_type === 'bearer') {
        payload.bearer_token = tcsConfig.bearer_token;
        payload.token_expiry = tcsConfig.token_expiry;
      } else {
        payload.username = tcsConfig.username;
        payload.password = tcsConfig.password;
      }

      if (tcsConfig.customer_no) {
        payload.customer_no = tcsConfig.customer_no;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/tcs/configure`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to configure TCS API');
      }

      toast.success('TCS API configured successfully!');
      setTcsConfigured(true);
      await fetchTcsConfig();
    } catch (error) {
      console.error('Error configuring TCS:', error);
      toast.error(error.message || 'Failed to configure TCS API');
    } finally {
      setSavingTcs(false);
    }
  };

  const handleSyncDtdc = async () => {
    setSyncingDtdc(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dtdc/sync?limit=100`, {
        method: 'POST'
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success(`✅ ${result.message}`, {
          description: `Synced ${result.synced_count} orders. ${result.failed_count} failed.`
        });
      } else {
        toast.error(result.message || 'DTDC sync failed');
      }
    } catch (error) {
      console.error('Error syncing DTDC:', error);
      toast.error('Failed to sync DTDC tracking');
    } finally {
      setSyncingDtdc(false);
    }
  };

  const handleSyncAllDtdc = async () => {
    setSyncingAllDtdc(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dtdc/sync-all?limit=500`, {
        method: 'POST'
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success(`✅ ${result.message}`, {
          description: `Synced ${result.synced_count} orders. ${result.failed_count} failed.`,
          duration: 5000
        });
      } else {
        toast.error(result.message || 'DTDC sync failed');
      }
    } catch (error) {
      console.error('Error syncing all DTDC:', error);
      toast.error('Failed to sync all DTDC tracking');
    } finally {
      setSyncingAllDtdc(false);
    }
  };

  const handleSyncTcs = async () => {
    setSyncingTcs(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tcs/bulk-sync?limit=100`, {
        method: 'POST'
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success(`✅ ${result.message}`, {
          description: `Synced ${result.synced_count} orders. ${result.failed_count} failed.`
        });
      } else {
        toast.error(result.message || 'TCS sync failed');
      }
    } catch (error) {
      console.error('Error syncing TCS:', error);
      toast.error('Failed to sync TCS tracking');
    } finally {
      setSyncingTcs(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password) {
      toast.error('Please fill in all fields');
      return;
    }

    if (passwordForm.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    setChangingPassword(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/agents/change-password`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: agent?.username,
            current_password: passwordForm.current_password,
            new_password: passwordForm.new_password
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to change password');
      }

      toast.success('Password changed successfully!');
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.message || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const getStoreStatus = (store) => {
    if (store.shopify_domain && store.shopify_token) {
      return {
        configured: true,
        icon: CheckCircle,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        label: 'Configured'
      };
    }
    return {
      configured: false,
      icon: XCircle,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      label: 'Not Configured'
    };
  };

  if (loading) {
    return (
      <div className="flex-1 bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="flex-1 bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-8 py-4">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-sm text-gray-500 mt-1">Configure integrations and system preferences</p>
      </div>
      
      <div className="p-8">
        <Tabs defaultValue="shopify" className="space-y-6">
          <TabsList className="grid w-full max-w-3xl grid-cols-3">
            <TabsTrigger value="shopify" className="flex items-center gap-2">
              <Store className="w-4 h-4" />
              Shopify Integration
            </TabsTrigger>
            <TabsTrigger value="tcs" className="flex items-center gap-2">
              <Truck className="w-4 h-4" />
              TCS API
            </TabsTrigger>
            <TabsTrigger value="security" className="flex items-center gap-2">
              <Key className="w-4 h-4" />
              Security
            </TabsTrigger>
          </TabsList>

          {/* Shopify Integration Tab */}
          <TabsContent value="shopify" className="space-y-6">
            {/* Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-1">Shopify Configuration</h3>
                <p className="text-sm text-blue-700">
                  Configure Shopify credentials for each store to enable order syncing, inventory management, and tracking updates.
                </p>
              </div>
            </div>

            {/* Store Configuration Cards */}
            <div className="grid gap-6">
          {stores.map(store => {
            const status = getStoreStatus(store);
            const StatusIcon = status.icon;
            const storeFormData = formData[store.store_name] || {};

            return (
              <Card key={store.store_name} className={`border-2 ${status.borderColor}`}>
                <CardHeader className={status.bgColor}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Store className="w-6 h-6 text-gray-700" />
                      <div>
                        <CardTitle className="text-xl">{store.store_name}</CardTitle>
                        <CardDescription className="text-sm mt-1">
                          {store.shop_url}
                        </CardDescription>
                      </div>
                    </div>
                    <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${status.bgColor} border ${status.borderColor}`}>
                      <StatusIcon className={`w-4 h-4 ${status.color}`} />
                      <span className={`text-sm font-medium ${status.color}`}>
                        {status.label}
                      </span>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="pt-6 space-y-4">
                  {store.last_synced_at && (
                    <div className="text-sm text-gray-600 mb-4">
                      Last synced: {new Date(store.last_synced_at).toLocaleString()}
                    </div>
                  )}

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor={`domain-${store.store_name}`}>
                        Shopify Domain
                      </Label>
                      <Input
                        id={`domain-${store.store_name}`}
                        type="text"
                        placeholder="yourstore.myshopify.com"
                        value={storeFormData.shopify_domain || ''}
                        onChange={(e) => handleInputChange(store.store_name, 'shopify_domain', e.target.value)}
                        className="font-mono text-sm"
                      />
                      <p className="text-xs text-gray-500">
                        Your Shopify store domain (e.g., yourstore.myshopify.com)
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`token-${store.store_name}`}>
                        Admin API Access Token
                      </Label>
                      <Input
                        id={`token-${store.store_name}`}
                        type="password"
                        placeholder="shpat_xxxxxxxxxxxxxxxxxxxxx"
                        value={storeFormData.shopify_token || ''}
                        onChange={(e) => handleInputChange(store.store_name, 'shopify_token', e.target.value)}
                        className="font-mono text-sm"
                      />
                      <p className="text-xs text-gray-500">
                        Your Shopify Admin API access token (starts with shpat_)
                      </p>
                    </div>
                    
                    {store.shopify_domain && store.shopify_token && (
                      <div className="space-y-3 border-t pt-4">
                        <p className="text-sm font-medium text-gray-700">Sync Options</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-gray-600 block mb-1">
                              Start from Order # (optional)
                            </label>
                            <input
                              type="number"
                              placeholder="e.g., 1001"
                              className="w-full px-3 py-2 border rounded-md text-sm"
                              value={syncOptions[store.store_name]?.startOrder || ''}
                              onChange={(e) => setSyncOptions(prev => ({
                                ...prev,
                                [store.store_name]: {
                                  ...prev[store.store_name],
                                  startOrder: e.target.value
                                }
                              }))}
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-600 block mb-1">
                              Sync Direction
                            </label>
                            <select
                              className="w-full px-3 py-2 border rounded-md text-sm"
                              value={syncOptions[store.store_name]?.direction || 'newest_first'}
                              onChange={(e) => setSyncOptions(prev => ({
                                ...prev,
                                [store.store_name]: {
                                  ...prev[store.store_name],
                                  direction: e.target.value
                                }
                              }))}
                            >
                              <option value="newest_first">Newest First (Default)</option>
                              <option value="oldest_first">Oldest First</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleSaveStore(store.store_name)}
                        disabled={saving === store.store_name}
                        className="flex-1 sm:flex-none"
                      >
                        {saving === store.store_name ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Saving & Testing...
                          </>
                        ) : (
                          'Save Configuration'
                        )}
                      </Button>
                      
                      {store.shopify_domain && store.shopify_token && (
                        <>
                          <Button
                            onClick={() => handleSyncStore(store.store_name)}
                            disabled={saving === `sync-${store.store_name}` || saving === `sync-all-${store.store_name}`}
                            variant="outline"
                            className="flex-1 sm:flex-none"
                          >
                            {saving === `sync-${store.store_name}` ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Syncing...
                              </>
                            ) : (
                              <>
                                <Package className="w-4 h-4 mr-2" />
                                Sync Recent
                              </>
                            )}
                          </Button>
                          
                          <Button
                            onClick={() => handleSyncAllComplete(store.store_name)}
                            disabled={saving === `sync-all-${store.store_name}` || saving === `sync-${store.store_name}`}
                            variant="default"
                            className="flex-1 sm:flex-none bg-green-600 hover:bg-green-700"
                          >
                            {saving === `sync-all-${store.store_name}` ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Syncing All...
                              </>
                            ) : (
                              <>
                                <Package className="w-4 h-4 mr-2" />
                                Sync All Complete
                              </>
                            )}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
            </div>

            {stores.length === 0 && (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <Store className="w-16 h-16 text-gray-400 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">No Stores Found</h3>
                  <p className="text-gray-500">No stores available to configure</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* TCS API Integration Tab */}
          <TabsContent value="tcs" className="space-y-6">
            {/* Info Banner */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-purple-900 mb-1">TCS API Configuration</h3>
                <p className="text-sm text-purple-700">
                  Configure TCS Pakistan courier API credentials to enable delivery tracking, COD payment status, and shipment updates.
                </p>
              </div>
            </div>

            {/* TCS Configuration Card */}
            <Card className={`border-2 ${tcsConfigured ? 'border-green-200' : 'border-gray-200'}`}>
              <CardHeader className={tcsConfigured ? 'bg-green-50' : 'bg-gray-50'}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Truck className="w-6 h-6 text-gray-700" />
                    <div>
                      <CardTitle className="text-xl">TCS Pakistan API</CardTitle>
                      <CardDescription className="text-sm mt-1">
                        Delivery tracking and COD payment integration
                      </CardDescription>
                    </div>
                  </div>
                  <div className={`flex items-center gap-2 px-3 py-1 rounded-full border ${
                    tcsConfigured 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-red-50 border-red-200'
                  }`}>
                    {tcsConfigured ? (
                      <>
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-green-600">Configured</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4 text-red-600" />
                        <span className="text-sm font-medium text-red-600">Not Configured</span>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>

              <CardContent className="pt-6 space-y-4">
                <div className="space-y-4">
                  {/* Authentication Type */}
                  <div className="space-y-2">
                    <Label>Authentication Type</Label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="auth_type"
                          value="bearer"
                          checked={tcsConfig.auth_type === 'bearer'}
                          onChange={(e) => handleTcsConfigChange('auth_type', e.target.value)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">Bearer Token</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="auth_type"
                          value="basic"
                          checked={tcsConfig.auth_type === 'basic'}
                          onChange={(e) => handleTcsConfigChange('auth_type', e.target.value)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">Username/Password</span>
                      </label>
                    </div>
                  </div>

                  {/* Bearer Token Fields */}
                  {tcsConfig.auth_type === 'bearer' && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="bearer_token">Bearer Token</Label>
                        <Input
                          id="bearer_token"
                          type="password"
                          placeholder="Enter your TCS API bearer token"
                          value={tcsConfig.bearer_token}
                          onChange={(e) => handleTcsConfigChange('bearer_token', e.target.value)}
                          className="font-mono text-sm"
                        />
                        <p className="text-xs text-gray-500">
                          Your TCS API bearer token for authentication
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="token_expiry">Token Expiry (Optional)</Label>
                        <Input
                          id="token_expiry"
                          type="datetime-local"
                          value={tcsConfig.token_expiry}
                          onChange={(e) => handleTcsConfigChange('token_expiry', e.target.value)}
                          className="text-sm"
                        />
                        <p className="text-xs text-gray-500">
                          When this token expires (leave empty if no expiry)
                        </p>
                      </div>
                    </>
                  )}

                  {/* Username/Password Fields */}
                  {tcsConfig.auth_type === 'basic' && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="tcs_username">Username</Label>
                        <Input
                          id="tcs_username"
                          type="text"
                          placeholder="TCS API username"
                          value={tcsConfig.username}
                          onChange={(e) => handleTcsConfigChange('username', e.target.value)}
                          className="font-mono text-sm"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="tcs_password">Password</Label>
                        <Input
                          id="tcs_password"
                          type="password"
                          placeholder="TCS API password"
                          value={tcsConfig.password}
                          onChange={(e) => handleTcsConfigChange('password', e.target.value)}
                          className="font-mono text-sm"
                        />
                      </div>
                    </>
                  )}

                  {/* Customer Number */}
                  <div className="space-y-2">
                    <Label htmlFor="customer_no">TCS Customer Number (Optional)</Label>
                    <Input
                      id="customer_no"
                      type="text"
                      placeholder="046809"
                      value={tcsConfig.customer_no}
                      onChange={(e) => handleTcsConfigChange('customer_no', e.target.value)}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500">
                      Your TCS customer number for COD payment tracking
                    </p>
                  </div>

                  <Button
                    onClick={handleSaveTcsConfig}
                    disabled={savingTcs}
                    className="w-full sm:w-auto"
                  >
                    {savingTcs ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving Configuration...
                      </>
                    ) : (
                      'Save TCS Configuration'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* TCS Pakistan Tracking Sync Card */}
            <Card className="border-2 border-purple-200">
              <CardHeader className="bg-purple-50">
                <div className="flex items-center gap-3">
                  <Truck className="w-6 h-6 text-purple-600" />
                  <div>
                    <CardTitle className="text-xl">TCS Pakistan Tracking Sync</CardTitle>
                    <CardDescription className="text-sm mt-1">
                      Bulk update delivery status for <strong>tnvcollectionpk</strong> orders from TCS Pakistan API
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="pt-6 space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">🚚 TCS Pakistan - What This Does:</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Fetches latest tracking status from <strong>TCS Pakistan API</strong></li>
                    <li>• Updates delivery status for <strong>tnvcollectionpk</strong> store</li>
                    <li>• Updates current location and delivery dates</li>
                    <li>• Processes up to 100 orders at once</li>
                  </ul>
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={handleSyncTcs}
                    disabled={syncingTcs || !tcsConfigured}
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                  >
                    {syncingTcs ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Syncing TCS Pakistan Orders...
                      </>
                    ) : (
                      <>
                        <Truck className="w-4 h-4 mr-2" />
                        Sync TCS Pakistan Tracking
                      </>
                    )}
                  </Button>
                </div>

                {!tcsConfigured && (
                  <div className="text-xs text-amber-600 bg-amber-50 rounded p-3 border border-amber-200">
                    <strong>⚠️ Note:</strong> Please configure TCS API credentials above before syncing.
                  </div>
                )}

                <div className="text-xs text-gray-500 bg-gray-50 rounded p-3">
                  <strong>Store:</strong> tnvcollectionpk | <strong>Courier:</strong> TCS Pakistan<br/>
                  Syncs only orders with tracking numbers that are not yet delivered. Individual tracking auto-updates when viewed.
                </div>
              </CardContent>
            </Card>

            {/* DTDC Courier Tracking Sync Card */}
            <Card className="border-2 border-orange-200">
              <CardHeader className="bg-orange-50">
                <div className="flex items-center gap-3">
                  <Package className="w-6 h-6 text-orange-600" />
                  <div>
                    <CardTitle className="text-xl">DTDC Courier Tracking Sync</CardTitle>
                    <CardDescription className="text-sm mt-1">
                      Bulk update delivery status for <strong>ashmiaa</strong> orders from DTDC Courier API
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="pt-6 space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">📦 DTDC Courier - What This Does:</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Fetches latest tracking status from <strong>DTDC Courier API</strong></li>
                    <li>• Updates delivery status for <strong>ashmiaa</strong> store</li>
                    <li>• Updates current location and delivery dates</li>
                    <li>• Auto-detects delivered orders and marks them</li>
                  </ul>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={handleSyncDtdc}
                    disabled={syncingDtdc || syncingAllDtdc}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    {syncingDtdc ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <Package className="w-4 h-4 mr-2" />
                        Sync Active Orders (100)
                      </>
                    )}
                  </Button>
                  
                  <Button
                    onClick={handleSyncAllDtdc}
                    disabled={syncingDtdc || syncingAllDtdc}
                    className="bg-orange-700 hover:bg-orange-800"
                  >
                    {syncingAllDtdc ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Syncing All...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Sync ALL Orders (500)
                      </>
                    )}
                  </Button>
                </div>

                <div className="text-xs text-gray-500 bg-gray-50 rounded p-3">
                  <strong>Store:</strong> ashmiaa | <strong>Courier:</strong> DTDC Courier<br/>
                  <strong>Active Orders:</strong> Syncs only non-delivered orders (faster)<br/>
                  <strong>ALL Orders:</strong> Syncs all orders including delivered (comprehensive)
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security/Password Change Tab */}
          <TabsContent value="security" className="space-y-6">
            {/* Info Banner */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-amber-900 mb-1">Account Security</h3>
                <p className="text-sm text-amber-700">
                  Change your password to keep your account secure. Use a strong password with at least 6 characters.
                </p>
              </div>
            </div>

            {/* Password Change Card */}
            <Card className="border-2">
              <CardHeader className="bg-gray-50">
                <div className="flex items-center gap-3">
                  <Key className="w-6 h-6 text-gray-700" />
                  <div>
                    <CardTitle className="text-xl">Change Password</CardTitle>
                    <CardDescription className="text-sm mt-1">
                      Update your account password
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="pt-6 space-y-4">
                {agent && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-900">
                      <span className="font-semibold">Current User:</span> {agent.username} ({agent.full_name})
                    </p>
                  </div>
                )}

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="current_password">Current Password</Label>
                    <Input
                      id="current_password"
                      type="password"
                      placeholder="Enter your current password"
                      value={passwordForm.current_password}
                      onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="new_password">New Password</Label>
                    <Input
                      id="new_password"
                      type="password"
                      placeholder="Enter new password (min 6 characters)"
                      value={passwordForm.new_password}
                      onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm_password">Confirm New Password</Label>
                    <Input
                      id="confirm_password"
                      type="password"
                      placeholder="Re-enter new password"
                      value={passwordForm.confirm_password}
                      onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                    />
                  </div>

                  <div className="pt-2">
                    <Button
                      onClick={handleChangePassword}
                      disabled={changingPassword}
                      className="w-full sm:w-auto"
                    >
                      {changingPassword ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Changing Password...
                        </>
                      ) : (
                        'Change Password'
                      )}
                    </Button>
                  </div>

                  <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <h4 className="font-semibold text-sm mb-2">Password Requirements:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Minimum 6 characters</li>
                      <li>• Use a combination of letters, numbers, and symbols</li>
                      <li>• Avoid common words or patterns</li>
                      <li>• Don&apos;t reuse old passwords</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Settings;